/**
 * The analytics events DDS fires for itself: `link_click`, `search` (Video Site
 * Search), `page_view` — which a modal raises because being shown is not a
 * navigation — the two dimensions a CTA reports for itself, `phone_lead` and
 * `pdf_download`, `error_displayed`, the three quiz `trackEvent`s, the two
 * Dynamic recommender `trackEvent`s and GA4 `select_item` — plus, for
 * WAT-12338, `pageLoad`, `view_item_list`, the Channel Lineup's own
 * differently-shaped `select_item` and a radio group's `trackEECEvent` — and,
 * for WAT-12600, the cart side sheet's GA4 `remove_from_cart` (mirroring the
 * analytics-service `fireRemoveFromCartEvent`). They share one queue and one timer, and the push goes to the PARENT frame's
 * `dataLayer` when it has one. One more, `fireFormResponse`, does not: it
 * reports through `DataMappingInterface`.
 *
 * ⚠ The payloads mirror the app's `fireCustomLinkClickGA`, `fireSearchEvent`
 * and `fireGa4ItemList` field for field, the WAT-12338 four mirror
 * `firePageLoadEvent`, `fireGa4ProductImpression`, the Channel Lineup's own
 * `handlePkgsHeaderCtaClick` and `fireCtaClickEventGA`, and the phone-lead and
 * PDF-download pair mirror the `fireDataLayerEvent` calls in video-lab's
 * `dtv-page-section-header`, reproduced rather than imported — like the
 * product recommender's quiz emitters — because DDS has zero
 * `@directv-digital/*` imports. A differently shaped event splits one tag's
 * data in two silently, so do not tidy a field name.
 *
 * ⚠ Ordering against app-fired events is NOT guaranteed: the app's own
 * analytics ride a post-paint FIFO this module cannot see, so a DDS push and an
 * app push raised in one interaction can land in either order. Among
 * themselves they keep call order.
 */

export interface LinkClickPayload {
  /** What the reader clicked, worded as it reads on screen. */
  linkName: string;
  /** Where on the page the control sits. Empty string when not given. */
  linkPosition?: string;
  /** Where the click leads. OMITTED FROM THE EVENT ENTIRELY when falsy. */
  linkDestination?: string;
}

/**
 * The named link positions. GTM reads "Footer" and "footer" as different
 * values, so a hand-typed literal at a call site shows up as a second
 * placement in the report. Use the constants.
 *
 * ⚠ The casing is MIXED on purpose. Reporting governance asks for lower case,
 * which is what the body position sends; `LINK_POSITION_FOOTER` keeps its
 * capital because no defect has been raised against that placement and moving
 * it books a second one. Neither is the pattern for the other.
 *
 * ⚠ NOTHING IN DDS LOWER-CASES THESE, on either route: this module pushes the
 * string as-is, and the atoms and organisms that carry these constants into
 * `data-linkposition` — `atoms/link` and `organisms/global-nav`'s own
 * `tracking` helper among them — write them as-is, so the constant IS the
 * reported placement both ways. The app's shared `getAnalyticsAttrs` lowers
 * the attributes it writes itself, which is what a capitalised value from here
 * would diverge from.
 */
export const LINK_POSITION_BODY = "body";
export const LINK_POSITION_FOOTER = "Footer";

/* The nav placements, lower case like LINK_POSITION_BODY and unlike
   LINK_POSITION_FOOTER: these are the spellings the reports already hold, and
   a tidier casing books a second placement rather than continuing the existing
   one. Which control sends which is the caller's business — the header splits
   them by width, top bar aside. */
export const LINK_POSITION_GLOBAL_NAV = "globalnav";
export const LINK_POSITION_GLOBAL_NAV_TOP_BAR = "globalnavtopbar";
export const LINK_POSITION_LEFT_NAV = "leftnav";

/**
 * The Channel Lineup search/sort panel's placement (WAT-12338, AN4/AN5/AN8).
 *
 * ⚠ THE SPACE AND THE LOWER CASE ARE BOTH THE REPORTS' OWN. Production writes
 * this literal on the sort radios, the suggestion rows, the chips and the Reset
 * control (`ChannelSearch.tsx:467`, `:506`, `DropdownList.tsx:204`), so every
 * row already on the layer is filed under it. `panel_channel_lineup` or
 * `panel channel lineup` books a second placement rather than continuing this
 * one, which is exactly what AC-10d's side-by-side diff is there to catch.
 *
 * ⚠ THE NAME THAT TRAVELS WITH IT IS LOWER CASE, AND THAT IS THE CALLER'S JOB.
 * Production folds it in its own name builders, and nothing on this path folds
 * it for you: `fireLinkClick` pushes `linkName` as given, and the container —
 * which names a click from `aria-label` — never lowercases either. So a
 * control under this placement lowercases its name where it BUILDS it (see
 * `lineup-analytics.shared.ts`), and keeps `data-linkposition` off itself so
 * the container cannot report the un-folded `aria-label` alongside. ⛔ The
 * reverse repair — spelling the analytics string into `aria-label` — is not
 * available: that attribute is the control's accessible name.
 */
export const LINK_POSITION_CHANNEL_LINEUP_PANEL = "panel_channel lineup";

/** A view toggle's placement (AN8) — production's own literal, from the
 *  legacy tab tray's `getAnalyticsAttrs(label, "component_tab", href)`. */
export const LINK_POSITION_COMPONENT_TAB = "component_tab";

/**
 * The events as they reach the layer. Named so `enqueue` — the one place every
 * typed event converges — still rejects a misspelt `event_name` or a
 * field the payload interfaces forbid. `linkDestination` is optional because it
 * is conditionally spread: a missing destination means the key is ABSENT.
 *
 * ⚠ `event` and `event_name` are BOTH "link_click", matching the name GTM's own
 * Link Click trigger pushes for an anchor, so a `<button>` and an anchor report
 * into one figure. The repo is split on the pairing — see the PR.
 */
interface LinkClickEvent {
  event: "link_click";
  event_name: "link_click";
  linkName: string;
  linkPosition: string;
  linkDestination?: string;
}

/**
 * ⚠ `searchTerm` and `searchCategory` are required; the other three are
 * CONDITIONALLY SPREAD in `fireSearch`, so a caller that names none of them
 * pushes the same three-key object. An absent key and a key holding "" are not
 * the same thing to GTM — it reports the empty string as a real value.
 */
interface SearchEvent {
  event: "search";
  searchTerm: string;
  searchCategory: string;
  searchSource?: string;
  errorMessage?: string;
  errorCode?: string;
}

/**
 * ⚠ `event` and `event_name` are BOTH "page_view" here, which is what the tag
 * triggers on. Both other fields are required, so neither is conditionally
 * spread.
 */
interface PageViewEvent {
  event: "page_view";
  event_name: "page_view";
  linkPosition: string;
  linkDestination: string;
}

/**
 * ⚠ `event` READS "trackEvent" AND `event_name` CARRIES THE NAME, unlike the
 * three events above where both read the same. That is the pairing production's
 * `DtvPageSectionHeader` pushes for these two (`dtv-page-section-header.tsx`
 * :93 and :95) and the pairing the live tags trigger on; matching
 * `link_click`'s shape instead would file every one of these under a trigger
 * nothing listens to.
 *
 * ⚠ `dim_name` is a FIXED STRING here and a derived page name on the download
 * below: the pair is a generic dimension slot, not two fields with one meaning.
 */
interface PhoneLeadEvent {
  event: "trackEvent";
  event_name: "phone_lead";
  dim_name: "call_type";
  dim_value: "new" | "mvp" | "support";
  linkPosition: "mobile" | "desktop";
}

/**
 * ⚠ NO `linkPosition`, where the phone lead carries one. Production sends the
 * download without it, so adding one would report a placement against rows that
 * have never held one.
 */
interface PdfDownloadEvent {
  event: "trackEvent";
  event_name: "pdf_download";
  dim_name: string;
  dim_value: string;
}

/** ⚠ `event` is "trackEvent" and the name rides in `event_name`, unlike
    `page_view`, where both carry it. The quiz tags trigger on this. */
interface QuizTrackEvent {
  event: "trackEvent";
  event_name: "quiz_status" | "quiz_question_status" | "quiz_recommendation_status";
  dim_name: string;
  dim_value: string;
}

/** The GA4 ecommerce item. ⚠ The empty strings and `quantity: 1` are part of
    the shape the item-list tag reads; an absent key is not the same row. */
interface SelectItemEcommerceItem {
  item_id: string;
  item_name: string;
  item_variant: "";
  affiliation: "";
  coupon: "";
  discount: "";
  index: number;
  item_list_id: string;
  item_list_name: string;
  price: string;
  quantity: 1;
}

interface SelectItemEvent {
  event: "select_item";
  ecommerce: { items: SelectItemEcommerceItem[] };
}

// Generated by Copilot- Start - 2026-10-06 11:14:48
/**
 * ⚠ `event` is "trackEvent" and `event_name` "error_displayed", unlike
 * `link_click`, `search` and `page_view`: the app's `fireErrorDisplayedGA`
 * pushes this pair, and its tag already triggers on it. No `dim_name`
 * (HIM-4776 AC11).
 */
interface ErrorDisplayedEvent {
  event: "trackEvent";
  event_name: "error_displayed";
  errorMessage: string;
  errorCode: string;
}
// Generated by Copilot- End - 2026-10-06 11:14:48

/**
 * A page identifying ITSELF, as distinct from `PageViewEvent`'s modal (WAT-12338
 * AN1). The six `page.*` keys are the ones production's `firePageLoadEvent` hands
 * its reporting layer, verbatim and dotted.
 *
 * ⛔ THE `event` / `event_name` PAIR IS DERIVED, NOT COPIED, and it is the one
 * field here nothing upstream can confirm. Production routes this payload through
 * `window.DataMappingInterface.init({ eventAction, eventCode, … })` rather than
 * through the data layer, and that layer is not loaded on the live page — which
 * is precisely why AN1 records the event as never delivered. There is therefore
 * NO existing data-layer row to match. The pair below is taken from the two
 * constants production names the call with (`EventActions.PAGE_LOAD` =
 * "pageLoad", `EventCode.PAGE_LOAD` = "Page_Load") rather than invented, but it
 * needs reporting's sign-off before AN1 can be called met.
 */
interface PageLoadEvent {
  event: "pageLoad";
  event_name: "Page_Load";
  "page.category.applicationName": string;
  "page.category.pageFunction": string;
  "page.category.pageOwnership": string;
  "page.pageInfo.flowCode": string;
  "page.pageInfo.lineOfBusiness": string;
  "page.location.url": string;
}

/**
 * A list of products becoming visible — AN2's impression, shaped exactly as the
 * app's `fireGa4ProductImpression` shapes it.
 *
 * ⚠ `items` IS A TOP-LEVEL KEY HERE, not nested under `ecommerce` the way
 * `SelectItemEvent` nests its own, and `currencyCode` sits beside it rather than
 * inside. Both are upstream's, and the impression tag reads that flat shape.
 */
interface ProductImpressionEvent {
  event: "view_item_list";
  currencyCode: "USD";
  items: ProductImpressionItem[];
}

/**
 * One chosen row of a named list (AN3's Shop click, AN6's Genre Pack Shop
 * click), shaped as the Channel Lineup's own `handlePkgsHeaderCtaClick` shapes
 * it — NOT as `SelectItemEvent` above does.
 *
 * ⛔ THREE FIELDS LOOK LIKE DEFECTS AND ARE THE CONTRACT. `ecommerce.items` is
 * an OBJECT and not an array; `affilation` is misspelt; `quantity` is the STRING
 * "1". All three are what `PackageCmprGrid.tsx:118-134` has been pushing, so all
 * three are what the live tag is already reading. AC-10d requires the variant's
 * parameter NAMES to match the control arm's exactly — correcting the spelling
 * here splits one tag's data in two and reads as a renamed parameter in the
 * side-by-side diff, which is the single failure that invalidates the test.
 *
 * ⚠ THE REASON THIS IS A SECOND FUNCTION rather than an option on
 * `fireSelectItem`: that one mirrors `fireGa4ItemList`, a different producer with
 * an array, the correct spelling, a numeric quantity and a formatted price, and
 * `organisms/product-recommender` reports through it. One shape cannot be both.
 */
interface ItemListSelectEvent {
  event: "select_item";
  ecommerce: {
    items: {
      item_id: string | number;
      item_name: string;
      item_variant: "";
      affilation: "";
      coupon: "";
      discount: "";
      index: number;
      item_list_id: string;
      item_list_name: string;
      price: "";
      quantity: "1";
    };
  };
}

/**
 * AN4's "radio-button product click" — the event `fusion.atoms.radio-button`
 * pushes for itself through `ReportingService.fireCtaClickEventGA`, reproduced
 * here because DDS's own radio groups are hand-written natives and import
 * nothing from `@directv-digital/*`.
 *
 * ⚠ `event` READS "trackEECEvent", which no other event in this module uses, and
 * the product sits under `ecommerce.click.products` rather than
 * `ecommerce.items`. Both are upstream's and both are what the tag triggers on.
 */
interface ProductClickEvent {
  event: "trackEECEvent";
  event_name: "product_click";
  ecommerce: {
    currencyCode: "USD";
    click: {
      products: {
        tag: string;
        /** ⚠ A product FIELD called `event`, inside an event. Upstream's. */
        event: string;
        event_name: string;
        quantity: 1;
      }[];
    };
  };
}

/** `modal_status`, as the app's `fireModalOpenCloseEventGA` pushes it. */
interface ModalStatusEvent {
  event: "trackEvent";
  event_name: "modal_status";
  dim_name: string;
  dim_value: string;
}

/** The Dynamic recommender's two status events, on the same dimension pair as the quiz's. */
interface RecommenderTrackEvent {
  event: "trackEvent";
  event_name: "recommender_selection_status" | "recommender_recommendation_status";
  dim_name: string;
  dim_value: string;
}

/* GA4 `remove_from_cart` — mirrors the analytics-service `fireRemoveFromCartEvent`
   the light cart sends on Remove all, field for field (`affilation` is its
   spelling; do not correct it). */
interface RemoveFromCartEvent {
  event: "remove_from_cart";
  ecommerce: {
    currency: string;
    value: number;
    items: Array<Record<string, string | number>>;
  };
}

type DataLayerEvent =
  | RemoveFromCartEvent
  | LinkClickEvent
  | ModalStatusEvent
  | SearchEvent
  | PageViewEvent
  | PageLoadEvent
  | PhoneLeadEvent
  | PdfDownloadEvent
  | ErrorDisplayedEvent
  | QuizTrackEvent
  | RecommenderTrackEvent
  | ProductImpressionEvent
  | ItemListSelectEvent
  | ProductClickEvent
  | SelectItemEvent;

let pending: DataLayerEvent[] = [];
let timer: ReturnType<typeof setTimeout> | undefined;
let frame: number | undefined;
let watching = false;

/** Where the push goes — the parent frame's layer when it has one, ours if not. */
function reportingWindow(): { dataLayer?: unknown[] } {
  try {
    /* Only retarget when the parent ALREADY HAS a layer — creating one in an
       outer frame that has none hands the event to nobody. */
    const parent = window.parent as unknown as
      | { dataLayer?: unknown[] }
      | undefined;
    if (parent && parent !== (window as unknown) && parent.dataLayer) {
      return parent;
    }
  } catch {
    /* Reading across a cross-origin frame boundary throws a SecurityError, so
       the read itself has to be guarded rather than the result checked. */
  }

  return window as unknown as { dataLayer?: unknown[] };
}

/**
 * Sends whatever is queued, right now.
 *
 * ⚠ It CANCELS the timer rather than re-arming one: called from an unload
 * signal the push has to be synchronous, because a timer armed at unload never
 * runs. Safe to call twice — the queue is taken before the push, and both
 * `pagehide` and `visibilitychange` fire on a navigation.
 */
function flush(): void {
  if (timer !== undefined) {
    clearTimeout(timer);
    timer = undefined;
  }
  if (frame !== undefined) {
    window.cancelAnimationFrame?.(frame);
    frame = undefined;
  }

  if (pending.length === 0) {
    return;
  }

  const queued = pending;
  pending = [];

  const target = reportingWindow();

  try {
    /* Created if absent: a click landing before GTM's container has run must
       not throw, and GTM adopts whatever array it finds. */
    target.dataLayer = target.dataLayer || [];
    for (const entry of queued) {
      target.dataLayer.push(entry);
    }
  } catch (error) {
    /* ⚠ A tag must never take the interaction with it. Logged rather than
       swallowed, so a non-pushable data layer is visible. */
    console.error("[DDS analytics] dataLayer push failed", error);
  }
}

/** `visibilitychange` fires both ways; only the hidden half is an ending. */
function flushWhenHidden(): void {
  if (window.document.visibilityState === "hidden") {
    flush();
  }
}

/**
 * Puts the two end-of-document signals up, once, on the first tracked event.
 *
 * ⚠ Both are needed and `unload` is not one of them: a listener on `unload` or
 * `beforeunload` disqualifies the page from the back/forward cache, while a
 * backgrounded mobile tab can be discarded without ever firing `pagehide`.
 * `visibilitychange` bubbles from Document to Window, so both bind to `window`.
 * Registered lazily because there is no `window` at module scope.
 */
function watchForTeardown(): void {
  if (watching || typeof window.addEventListener !== "function") {
    return;
  }

  watching = true;
  window.addEventListener("pagehide", flush);
  window.addEventListener("visibilitychange", flushWhenHidden);
}

/**
 * Pushes a `link_click` onto the GTM data layer.
 *
 * ⚠ ALMOST NOTHING SHOULD CALL THIS. The container reports a `link_click` for
 * a click whose element, or nearest ancestor carrying `data-linkposition` /
 * `data-linkdestination`, yields at least two of placement, name and
 * destination — the name read from `aria-label` or text, never lowercased.
 * Reach for those attributes first. Push from here only for a report the
 * container cannot make (HIM-4776: a lowercased or renamed `linkName`), and
 * then keep those attributes OFF the element, or the click is reported twice.
 *
 * Safe to import from a Server Component — nothing reads a browser global until
 * a call comes in. ⚠ Self-deferred past the next paint so GTM's response stays off the
 * interaction frame (INP), which loses the push outright if the caller then
 * navigates: that is what `flush` and the two unload signals recover. Do not
 * queue this behind another defer wrapper; it already defers itself.
 */
export function fireLinkClick({
  linkName,
  linkPosition,
  linkDestination
}: LinkClickPayload): void {
  if (typeof window === "undefined") {
    return;
  }

  enqueue({
    event: "link_click",
    event_name: "link_click",
    /* linkName is pushed UNGATED by design - unlike linkDestination below it is
       neither trimmed nor dropped here, so the wording a call site passes is the
       wording GTM reports. Every caller gates it instead; grep fireLinkClick. */
    linkName,
    linkPosition: linkPosition || "",
    /* Conditionally spread: a missing destination means the key is ABSENT, not
       present and empty, which GTM would report as a real value. */
    ...(linkDestination && { linkDestination })
  });
}

/**
 * What the reader searched for, and which search it was. Two required fields
 * and three optional ones. ⚠ There is no default category and none worth
 * guessing — a DDS field has no idea what it is searching, and a wrong category
 * files the search under someone else's feature. The call site names it.
 */
export interface SearchPayload {
  /**
   * The submitted query, VERBATIM — not lower-cased here. Callers normalise
   * inconsistently at their own end, so folding case here would
   * double-transform one caller's data and change another's.
   */
  searchTerm: string;
  /**
   * Which search this was — `"aiSearch"`, `"channelLineup"`. It reads like a
   * fixed vocabulary and is not one: a host can put whatever a feed hands it
   * here, so it is bounded at REPORTED_FIELD_MAX_LENGTH like the rest.
   */
  searchCategory: string;
  /**
   * Which search SURFACE raised it — `"globalsearch"` for the nav panel.
   *
   * ⚠ A different axis from `searchCategory`, which says which COMPONENT ran
   * the search. One global-search panel reports two categories against one
   * source, and the split is the point.
   */
  searchSource?: string;
  /**
   * How the search's own backend answered — `"success"`, or the failure message
   * the backend returned.
   *
   * ⚠ AI search only, and not a UI error state; a standard search has no such
   * call and omits both this and `errorCode`. The call site owns the wording
   * AND the default — this module never sees the promise, so defaulting to
   * "success" here would report a success for a call that never happened.
   */
  errorMessage?: string;
  /**
   * The backend's status, as a STRING — `"200"` on success. GTM dimensions are
   * strings, and a numeric 200 and the string "200" report as two values.
   */
  errorCode?: string;
  /**
   * How many suggestions were on screen when this search was reported. The
   * only way a zero-results search is distinguishable from any other — the
   * event is otherwise identical (HIM-4761 AC-17).
   *
   * ⚠ A NUMBER, where `errorCode` beside it is deliberately a string. That
   * rule is about DIMENSIONS, which GTM compares as text; a count is a METRIC
   * and has to arrive numeric or nothing downstream can average or threshold
   * it. `0` is a real value and must survive — see the finite-number guard at
   * the push rather than the `isPresent` guard the string fields use, which
   * would be a falsiness test and would drop exactly the zero-results case
   * this field exists to report.
   */
  searchResultCount?: number;
}

/**
 * The longest search term this module will push. ⚠ The AI field carries
 * reader-authored free text onto `window.dataLayer` — a page-global object, and
 * the PARENT frame's when DDS is embedded. GA4 truncates an event parameter at
 * 100 characters anyway, so pushing more widens the exposure and reports
 * nothing extra.
 */
const SEARCH_TERM_MAX_LENGTH = 100;

/**
 * The same bound on every other field of the search event — `searchCategory`,
 * `searchSource`, `errorMessage` and `errorCode`. None of the four is fully
 * controlled by the call site: a backend's wording and a host-supplied string
 * both reach them.
 */
const REPORTED_FIELD_MAX_LENGTH = 100;

/**
 * Supplied, and not just whitespace.
 *
 * ⚠ This is also the null guard. `strictNullChecks` is off repo-wide, so a
 * missing term type-checks and a bare `.slice()` on it would throw inside a
 * submit handler before `preventDefault()` runs — which does not cancel the
 * navigation. ⚠ And it tests the TYPE, not truthiness: a host reading
 * `response.status` into `errorCode` writes a number, and `.trim` is not there.
 */
function isBlank(candidate?: string): boolean {
  return typeof candidate !== "string" || candidate.trim() === "";
}

/* The same rule as a type guard, so the conditional spreads below narrow even
   though `strictNullChecks` is off today. */
function isPresent(candidate?: string): candidate is string {
  return !isBlank(candidate);
}

/**
 * Pushes a `search` event onto the GTM data layer — the Video Site Search
 * event, shaped as the app's own `fireSearchEvent` shapes it.
 *
 * ⚠ Shares the queue, the timer and both unload signals with `fireLinkClick`
 * through `enqueue`: a search submitted from a real `<form>` NAVIGATES, so the
 * flush is the only thing that keeps it from being dropped.
 */
export function fireSearch({
  searchTerm,
  searchCategory,
  searchSource,
  errorMessage,
  errorCode,
  searchResultCount
}: SearchPayload): void {
  if (typeof window === "undefined") {
    return;
  }

  /* ⚠ Blank is absent on both required fields: a whitespace category books a
     dimension nobody queries, and a term-less search inflates volume with
     nothing to read. Neither field is trimmed for the push — the term stays
     verbatim; the trim only decides whether there is anything to report. */
  if (isBlank(searchTerm) || isBlank(searchCategory)) {
    return;
  }

  /* ⚠ The bare `.slice()` calls are safe only because `isBlank` ran above. */
  enqueue({
    event: "search",
    searchTerm: searchTerm.slice(0, SEARCH_TERM_MAX_LENGTH),
    searchCategory: searchCategory.slice(0, REPORTED_FIELD_MAX_LENGTH),
    /* Spread rather than assigned: a key present-and-empty is a value GTM
       reports. `isBlank` rather than truthiness, so a CMS field that
       round-trips a space cannot book a dimension nobody queries. */
    ...(isPresent(searchSource) && {
      searchSource: searchSource.slice(0, REPORTED_FIELD_MAX_LENGTH)
    }),
    ...(isPresent(errorMessage) && {
      errorMessage: errorMessage.slice(0, REPORTED_FIELD_MAX_LENGTH)
    }),
    ...(isPresent(errorCode) && {
      errorCode: errorCode.slice(0, REPORTED_FIELD_MAX_LENGTH)
    }),
    /* ⚠ A finite-number guard, NOT `isPresent` and not truthiness: 0 is the
       whole point of the field, and `NaN` and `Infinity` both serialise into
       the data layer and neither is a count. */
    ...(typeof searchResultCount === "number" &&
      Number.isFinite(searchResultCount) && {
      searchResultCount: Math.max(0, Math.trunc(searchResultCount))
    })
  });
}

/**
 * A modal being shown, reported as its own page view — it is not a navigation,
 * so nothing in the platform reports it.
 *
 * ⚠ `linkPosition` and `linkDestination` are link-click vocabulary and look
 * wrong on a page view; they are what the tag consumes. They carry the modal's
 * identity and its virtual path. Renaming them breaks the tag silently.
 */
export interface PageViewPayload {
  /** The modal's identity, formatted `modal_<unique modal name>`. */
  linkPosition: string;
  /** The modal's virtual path — the page's own path, then the modal name. */
  linkDestination: string;
}

export function firePageView({ linkPosition, linkDestination }: PageViewPayload): void {
  if (typeof window === "undefined") {
    return;
  }

  /* `|| ""` on both because `strictNullChecks` is off, so an `undefined` can
     still reach here from either producer. Uncoerced, the beacon fires, the row
     looks normal, and one field is the string "undefined". */
  enqueue({
    event: "page_view",
    event_name: "page_view",
    linkPosition: linkPosition || "",
    linkDestination: linkDestination || ""
  });
}

// Generated by Copilot- Start - 2026-10-06 11:14:48
/** An error the reader was shown: the copy as displayed, and the backend's status. */
export interface ErrorDisplayedPayload {
  /** The message as it rendered on screen. */
  errorMessage: string;
  /** The backend's status as a STRING, for the same reason `SearchPayload.errorCode` is one. */
  errorCode: string;
}

/**
 * Pushes `error_displayed`, shaped as the app's `fireErrorDisplayedGA` shapes
 * it. ⚠ Call it when the message RENDERS, not when the request fails: a
 * failure the reader never sees is not reported. A blank message is dropped.
 */
export function fireErrorDisplayed({ errorMessage, errorCode }: ErrorDisplayedPayload): void {
  if (typeof window === "undefined" || isBlank(errorMessage)) {
    return;
  }

  enqueue({
    event: "trackEvent",
    event_name: "error_displayed",
    errorMessage: errorMessage.slice(0, REPORTED_FIELD_MAX_LENGTH),
    errorCode: isPresent(errorCode) ? errorCode.slice(0, REPORTED_FIELD_MAX_LENGTH) : "unknown"
  });
}
// Generated by Copilot- End - 2026-10-06 11:14:48

/** A modal opening or closing: `dim_name` is the transition, `dim_value` the modal. */
export interface ModalStatusPayload {
  dimName: "open" | "close";
  dimValue: string;
}

/**
 * Pushes `modal_status`, shaped as the app's `fireModalOpenCloseEventGA`
 * shapes it. A blank modal name is dropped.
 */
export function fireModalStatus({ dimName, dimValue }: ModalStatusPayload): void {
  if (typeof window === "undefined" || isBlank(dimValue)) {
    return;
  }

  enqueue({
    event: "trackEvent",
    event_name: "modal_status",
    dim_name: dimName,
    dim_value: dimValue
  });
}

export interface RemoveFromCartPayload {
  /** The cart's monthly subtotal. */
  value: number;
  /** The cart package's SKU, name and price — what the light cart reports. */
  itemId?: string;
  itemName?: string;
  price?: number | string;
}

/** The cart was emptied with Remove all. One item, the package, as the light
 *  cart's `handleRemoveCartClick` reports it; the empty fields are its own. */
export function fireRemoveFromCart({ value, itemId, itemName, price }: RemoveFromCartPayload): void {
  if (typeof window === "undefined") {
    return;
  }

  enqueue({
    event: "remove_from_cart",
    ecommerce: {
      currency: "USD",
      value: Number.isFinite(value) ? value : 0,
      items: [{
        item_id: itemId ?? "",
        item_name: itemName ?? "",
        item_category: "",
        item_variant: "",
        affilation: "",
        coupon: "",
        discount: "",
        index: "",
        item_list_id: "",
        item_list_name: "",
        price: price ?? "",
        quantity: "1"
      }]
    }
  });
}

/** Reported text: lower case, whitespace collapsed, so a stray newline in a CMS
    label does not split one value into several rows. ⚠ Not for URLs. */
export function analyticsText(value?: string | null): string {
  return (value || "").replace(/\s+/g, " ").trim().toLowerCase();
}

/** What a blank label reports as. Dropping it instead would leave "2 of 4 - ",
    or lose the control's whole link set. */
export const UNTITLED_LABEL = "(untitled)";

/** Stands in for a recommended card with no id, so the card count survives. */
const UNKNOWN_ITEM_ID = "unknown";

/** `[progress] - [question label]`, lower case: `quiz_question_status`'s `dim_value`
    and the tail of an option's link position. `progressLabel` is the whole "2 of 4". */
export function quizQuestionDimValue(progressLabel: string, questionLabel?: string): string {
  return `${analyticsText(progressLabel)} - ${analyticsText(questionLabel) || UNTITLED_LABEL}`;
}

/** What GTM's Link Click trigger reads off a control or its nearest ancestor.
    `data-linkname` is parity with the app's payload; GTM names from `aria-label`. */
export type LinkClickAttrs = {
  "data-linkname": string;
  "data-linkposition": string;
  "data-linkdestination": string;
};

/** The three attributes for one control, lower case. ⚠ Returns NOTHING without a
    destination, so a first paint with no `window` yet cannot emit a half set. */
export function linkClickAttrs(
  linkName: string,
  linkPosition: string,
  linkDestination: string
): Partial<LinkClickAttrs> {
  if (!linkDestination) {
    return {};
  }

  return {
    "data-linkname": analyticsText(linkName) || UNTITLED_LABEL,
    "data-linkposition": (linkPosition || "").toLowerCase(),
    "data-linkdestination": linkDestination.toLowerCase()
  };
}

export interface QuizStatusPayload {
  status: "start" | "complete";
  /** The quiz's tool id, pushed as given — the same string its link positions are built on. */
  quizName: string;
}

export function fireQuizStatus({ status, quizName }: QuizStatusPayload): void {
  if (typeof window === "undefined") {
    return;
  }

  enqueue({
    event: "trackEvent",
    event_name: "quiz_status",
    dim_name: status,
    dim_value: quizName
  });
}

export interface QuizQuestionStatusPayload {
  status: "displayed" | "answered";
  /** The step indicator's own "2 of 4", so the report matches what was shown. */
  progressLabel: string;
  /** The question as shown. Blank reports as `UNTITLED_LABEL`. */
  questionLabel?: string;
}

export function fireQuizQuestionStatus({
  status,
  progressLabel,
  questionLabel
}: QuizQuestionStatusPayload): void {
  if (typeof window === "undefined") {
    return;
  }

  enqueue({
    event: "trackEvent",
    event_name: "quiz_question_status",
    dim_name: status,
    dim_value: quizQuestionDimValue(progressLabel, questionLabel)
  });
}

export interface QuizRecommendationStatusPayload {
  /** One id per displayed card, in display order. A blank id reports as "unknown". */
  itemIds: string[];
}

/** Pushes nothing for an empty list; any non-empty list reports, even all-unknown. */
export function fireQuizRecommendationStatus({
  itemIds
}: QuizRecommendationStatusPayload): void {
  if (typeof window === "undefined") {
    return;
  }

  const ids = itemIds || [];
  if (ids.length === 0) {
    return;
  }

  enqueue({
    event: "trackEvent",
    event_name: "quiz_recommendation_status",
    dim_name: "displayed",
    dim_value: ids.map(id => id || UNKNOWN_ITEM_ID).join("|")
  });
}

export interface RecommenderStatusPayload {
  eventName: RecommenderTrackEvent["event_name"];
  /** The categories reported, `|`-joined. */
  dimName: string;
  dimValue: string;
}

/** Pushes one Dynamic recommender status, verbatim: the caller builds both dimensions. */
export function fireRecommenderStatus({ eventName, dimName, dimValue }: RecommenderStatusPayload): void {
  if (typeof window === "undefined") {
    return;
  }

  enqueue({
    event: "trackEvent",
    event_name: eventName,
    dim_name: dimName,
    dim_value: dimValue
  });
}

export interface SelectItemPayload {
  /** The card's title as shown; reported lower case as both item id and name. */
  itemName: string;
  /** The card's position in the list, as the caller numbers it. */
  index: number;
  /** Reported to two decimals; anything non-finite reports as "". */
  price: number | null;
  /** Reported as both `item_list_id` and `item_list_name`, as given. */
  itemListName: string;
}

/** A GA4 `select_item` for one chosen card. ⚠ No `{ ecommerce: null }` clearing
    push goes first: the app's `fireGa4ItemList` sends none. */
export function fireSelectItem({
  itemName,
  index,
  price,
  itemListName
}: SelectItemPayload): void {
  if (typeof window === "undefined") {
    return;
  }

  const reportedName = (itemName || "").toLowerCase();

  enqueue({
    event: "select_item",
    ecommerce: {
      items: [
        {
          item_id: reportedName,
          item_name: reportedName,
          item_variant: "",
          affiliation: "",
          coupon: "",
          discount: "",
          index,
          item_list_id: itemListName,
          item_list_name: itemListName,
          price: Number.isFinite(price) ? price.toFixed(2) : "",
          quantity: 1
        }
      ]
    }
  });
}

/**
 * The width below which a phone lead is reported as a mobile one.
 *
 * ⚠ PRODUCTION'S OWN NUMBER, and it is neither the MUI scale the rest of that
 * component measures against (600 / 1200) nor DDS's breakpoints (735 / 1280 /
 * 1732). It buckets a REPORT, not a layout: every mobile-against-desktop phone
 * lead already on the layer was cut at 768, so moving it to a real breakpoint
 * re-cuts that comparison against a boundary the historical rows were never
 * measured at. Do not "fix" it.
 */
const PHONE_LEAD_MOBILE_MAX_WIDTH = 768;

/** What a download reports when the path has no segment to name it by — the
 *  site root is the live case, and this is production's own wording for it. */
const PDF_DIM_NAME_FALLBACK = "home";

/**
 * Which audience a call belongs to, read off the CTA's wording.
 *
 * ⚠ THE ON-SCREEN WORDING, NEVER THE ACCESSIBLE NAME. A CTA carrying its own
 * authored `*_cta_aria_label` would re-bucket its calls the moment the two
 * diverge, and every row already reported was cut on the wording.
 *
 * ⚠ The tests are SUBSTRING and case-folded, and "support" wins over "mvp" —
 * production's order, which is what decides where a label holding both lands.
 */
function phoneLeadCallType(ctaLabel?: string): PhoneLeadEvent["dim_value"] {
  /* Type-tested rather than coerced, for the reason `isBlank` gives above: with
     `strictNullChecks` off a missing label reaches here, and `.toLowerCase()`
     on it would throw inside the click handler. */
  const wording = typeof ctaLabel === "string" ? ctaLabel.toLowerCase() : "";

  if (wording.includes("support")) {
    return "support";
  }

  if (wording.includes("mvp")) {
    return "mvp";
  }

  return "new";
}

/** The page a download was taken from: the last segment of its path, or the
 *  fallback. Guarded because the `location` an embedding frame or a test
 *  supplies is not required to carry a `pathname`. */
function pdfDimName(): string {
  const path = window.location && window.location.pathname;
  const segment =
    typeof path === "string" ? path.split("/").filter(Boolean).pop() : undefined;

  return segment || PDF_DIM_NAME_FALLBACK;
}

/**
 * A CTA that dials rather than navigates.
 *
 * ⚠ ONE FIELD, because everything else the event carries is DERIVED here: the
 * audience from the wording, the placement from the viewport. Passed in, two
 * call sites on one page would be free to bucket the same click differently.
 */
export interface PhoneLeadPayload {
  /** The CTA's wording as it reads on screen, which sorts the call into
   *  `support`, `mvp` or `new`. Absent or blank, `new`. */
  ctaLabel?: string;
}

/**
 * Pushes a `phone_lead` onto the GTM data layer — a click on a `tel:` CTA,
 * which the container reports as an ordinary link click and cannot tell apart
 * from a navigation.
 *
 * ⚠ THIS IS NOT A SECOND REPORT OF THE CLICK. The container still raises its
 * own `link_click` off the anchor; this carries the call-type dimension beside
 * it, so a caller must not raise a `link_click` of its own as well.
 */
export function firePhoneLead({ ctaLabel }: PhoneLeadPayload): void {
  if (typeof window === "undefined") {
    return;
  }

  enqueue({
    event: "trackEvent",
    event_name: "phone_lead",
    dim_name: "call_type",
    dim_value: phoneLeadCallType(ctaLabel),
    /* `<` rather than `<=`, and a viewport reporting no width reads as desktop.
       Both are production's, and both decide which bucket a row lands in. */
    linkPosition: window.innerWidth < PHONE_LEAD_MOBILE_MAX_WIDTH ? "mobile" : "desktop"
  });
}

/**
 * What was downloaded. ⚠ One field for the same reason as the phone lead: the
 * page it was taken from is read here rather than passed, so two CTAs on one
 * page cannot name that page differently.
 */
export interface PdfDownloadPayload {
  /** What the reader clicked, worded as it reads on screen — a caller with no
   *  wording to report falls back to the document's address. Absent, the key is
   *  still pushed and reads empty. */
  documentName?: string;
}

/**
 * Pushes a `pdf_download` onto the GTM data layer — a click on a CTA whose
 * destination is a document rather than a page, which nothing else reports as a
 * download.
 *
 * ⚠ Same terms as `firePhoneLead`: it sits BESIDE the container's own
 * `link_click` and does not replace it.
 */
export function firePdfDownload({ documentName }: PdfDownloadPayload): void {
  if (typeof window === "undefined") {
    return;
  }

  enqueue({
    event: "trackEvent",
    event_name: "pdf_download",
    dim_name: pdfDimName(),
    /* Coerced rather than spread: production pushes the key unconditionally, and
       with `strictNullChecks` off an unset name would otherwise reach the layer
       as the literal string "undefined", which a report groups by as happily as
       a real one. */
    dim_value: documentName || ""
  });
}

/**
 * How a page classifies ITSELF (WAT-12338 AN1) — the five fixed dimensions, with
 * the page's own path read here rather than passed.
 *
 * ⚠ THE FIVE ARE THE CALLER'S, THE PATH IS NOT, for the reason `firePhoneLead`
 * and `firePdfDownload` give for their own derived fields: two call sites on one
 * page must not be free to name that page differently. The five classifications
 * are a property of the PAGE and nothing in a design-system module knows them —
 * "UF", "Video" and "Prospect" are facts about the host, so guessing them here
 * would file someone else's page under this one's audience.
 */
export interface PageLoadPayload {
  /** `page.category.applicationName` — "UF" on an Upper Funnel page. */
  applicationName: string;
  /** `page.category.pageFunction` — "Other" on the channel lineup. */
  pageFunction: string;
  /** `page.category.pageOwnership` — "Video". */
  pageOwnership: string;
  /** `page.pageInfo.flowCode` — "Prospect". */
  flowCode: string;
  /** `page.pageInfo.lineOfBusiness` — "Video". */
  lineOfBusiness: string;
}

/** The page's own path. Guarded for the same reason `pdfDimName` guards: the
 *  `location` an embedding frame or a test supplies need carry no `pathname`. */
function pagePath(): string {
  const path = window.location && window.location.pathname;
  return typeof path === "string" ? path : "";
}

/**
 * Pushes a page-load onto the GTM data layer — AN1's "each time the Signature
 * packages grid is shown, a page-load event is sent".
 *
 * ⛔ IT IS NOT A NAVIGATION, and that is why it is here rather than left to the
 * platform: the grid is shown on load AND on a view switch that changes no URL,
 * so nothing else reports the second one. Call it on BOTH, which is what "each
 * time… is shown" asks for.
 *
 * ⚠ THE `event` / `event_name` PAIR IS UNVERIFIED — see `PageLoadEvent`.
 * Everything else here is upstream's field for field.
 */
export function firePageLoad({
  applicationName,
  pageFunction,
  pageOwnership,
  flowCode,
  lineOfBusiness
}: PageLoadPayload): void {
  if (typeof window === "undefined") {
    return;
  }

  enqueue({
    event: "pageLoad",
    event_name: "Page_Load",
    "page.category.applicationName": applicationName,
    "page.category.pageFunction": pageFunction,
    "page.category.pageOwnership": pageOwnership,
    "page.pageInfo.flowCode": flowCode,
    "page.pageInfo.lineOfBusiness": lineOfBusiness,
    "page.location.url": pagePath()
  });
}

/** One product in an impression. ⚠ `id` and `position`, NOT the GA4 item
 *  shape `fireSelectItem` uses — the impression tag reads these two names. */
export interface ProductImpressionItem {
  /** The product's analytics id. A product without one is never in this list
   *  — the CALLER drops it, because dropping also renumbers (AN2). */
  id: string;
  /** 1-based. ⚠ AN3's `select_item` counts its columns from 0 instead; the
   *  two are deliberately different and neither follows the other. */
  position: number;
}

/**
 * Pushes a `view_item_list` onto the GTM data layer — AN2's product impression,
 * shaped as the app's `fireGa4ProductImpression` shapes it.
 *
 * ⚠ AN EMPTY LIST PUSHES NOTHING. Upstream gates on the filtered list's length
 * (`ChnlListingPage.tsx:58-68`), so a view whose every package lacks an
 * analytics id reports no impression at all rather than an impression of none.
 */
export function fireProductImpression({ items }: { items: ProductImpressionItem[] }): void {
  if (typeof window === "undefined") {
    return;
  }

  const reported = items || [];
  if (reported.length === 0) {
    return;
  }

  enqueue({
    event: "view_item_list",
    currencyCode: "USD",
    items: reported
  });
}

/** One chosen row of a named list. See `ItemListSelectEvent` for why this is a
 *  second shape rather than an option on `SelectItemPayload`. */
export interface ItemListSelectPayload {
  /** AN3 sends the column's 0-based position here, AN6 the pack's analytics
   *  id — the caller owns the identity, this module reports it unchanged. */
  itemId: string | number;
  /** ⚠ THE BUTTON'S LABEL, not the product's name: upstream passes
   *  `ctaProps?.label` ("Shop"). Reported as given, NOT lower-cased. */
  itemName: string;
  /** The row's position in the list, as the caller numbers it. */
  index: number;
  /** Reported as `item_list_id`; distinct from the name beside it. */
  itemListId: string;
  /** Reported as `item_list_name`. */
  itemListName: string;
}

/** A `select_item` for one chosen row of a named list. ⚠ No `{ ecommerce: null }`
 *  clearing push goes first — upstream sends none. */
export function fireItemListSelect({
  itemId,
  itemName,
  index,
  itemListId,
  itemListName
}: ItemListSelectPayload): void {
  if (typeof window === "undefined") {
    return;
  }

  enqueue({
    event: "select_item",
    ecommerce: {
      items: {
        item_id: itemId,
        item_name: itemName,
        item_variant: "",
        /* Misspelt upstream, and therefore misspelt here — see the interface. */
        affilation: "",
        coupon: "",
        discount: "",
        index,
        item_list_id: itemListId,
        item_list_name: itemListName,
        price: "",
        quantity: "1"
      }
    }
  });
}

/** The three fields a radio group reports about itself (AN4). */
export interface ProductClickPayload {
  /** Which kind of control reported — "Radiobutton". */
  tag: string;
  /** ⚠ Pushed under the product key `event`, NOT the envelope's — "Checked". */
  productEvent: string;
  /** ⚠ Pushed under the product key `event_name` — "Radiobutton". */
  productEventName: string;
}

/**
 * Pushes a `trackEECEvent` / `product_click` onto the GTM data layer — the
 * report `fusion.atoms.radio-button` makes for itself, which AN4 requires a
 * DDS-native radio group to keep making.
 *
 * ⚠ IT SITS BESIDE THE GROUP'S OWN `link_click`, exactly as it does upstream,
 * and does not replace it: one sort change reports both.
 */
export function fireProductClick({
  tag,
  productEvent,
  productEventName
}: ProductClickPayload): void {
  if (typeof window === "undefined") {
    return;
  }

  enqueue({
    event: "trackEECEvent",
    event_name: "product_click",
    ecommerce: {
      currencyCode: "USD",
      click: {
        products: [
          {
            tag,
            event: productEvent,
            event_name: productEventName,
            quantity: 1
          }
        ]
      }
    }
  });
}

/**
 * Queues one entry and makes sure something will send it. ⚠ All three steps are
 * a unit: skipping `watchForTeardown` loses every event raised on a page that
 * then navigates, and both failure modes are silent.
 */
function enqueue(entry: DataLayerEvent): void {
  pending.push(entry);

  watchForTeardown();

  /* One schedule for the queue, not one per call: a second event in the same
     turn joins the batch the first one armed. */
  if (timer === undefined && frame === undefined) {
    scheduleFlush();
  }
}

/**
 * Sends the queue AFTER the next paint, not merely after this task: GTM runs
 * its tags synchronously inside the push, and a plain zero timeout lands
 * before the frame, so the click waited for every tag (1–3s on test3). A frame
 * callback then a timeout puts the push behind the paint. A hidden tab paints
 * no frames, so it falls back to the timeout alone.
 */
function scheduleFlush(): void {
  const requestFrame = window.requestAnimationFrame;
  if (typeof requestFrame !== "function" || window.document?.visibilityState === "hidden") {
    timer = setTimeout(flush, 0);
    return;
  }
  frame = requestFrame.call(window, () => {
    frame = undefined;
    timer = setTimeout(flush, 0);
  });
}

/**
 * What a form-response event carries. `eventCode`, `linkName` and `linkPosition`
 * name the form; the rest report how the request ended.
 */
export interface FormResponsePayload {
  /** The reporting code for this form, e.g. the local-channel search's. */
  eventCode: string;
  linkName: string;
  linkPosition: string;
  /** "1" on success, "0" on failure — strings, as the report expects. */
  successFlag: string;
  statusCode?: number;
  errorType?: string;
  statusMessage?: string;
}

/* The legacy reporter polls for the tag-manager-injected `DataMappingInterface`:
   every 50 ms, and it checks once more on the tick that exceeds 100 — 101 polls,
   about 5.05 s, then the event is dropped. */
/**
 * The tag-manager-injected reporter. It is on no `Window` type this repo owns,
 * so it is read through a narrow cast — the same shape `dataLayerHost` uses
 * above — rather than `any`, which would also silence a typo in `init`.
 */
interface DataMappingInterface {
  init: (event: Record<string, unknown>) => void;
}

const dataMappingInterface = (): DataMappingInterface | undefined => (
  window as unknown as { DataMappingInterface?: DataMappingInterface }
).DataMappingInterface;

const DMI_POLL_MS = 50;
const DMI_MAX_TICKS = 100;

/**
 * Reports a form response through `DataMappingInterface`, the channel the
 * legacy `fireFormAction("FORM_RESPONSE")` uses — not the data layer, so it
 * does not share the link-click queue. The action is `formResponse` and the
 * code `Form_Response`; the form's own code travels inside `additionaldata`.
 *
 * ⚠ If the interface has not loaded it is polled for, then dropped silently,
 * the same as production. Never throws: a reporting failure must not break the
 * request that raised it.
 */
export function fireFormResponse(payload: FormResponsePayload): void {
  if (typeof window === "undefined" || !payload || Object.keys(payload).length === 0) {
    return;
  }

  const send = () => {
    try {
      dataMappingInterface()?.init({
        eventAction: "formResponse",
        eventCode: "Form_Response",
        additionaldata: { ...payload },
        datatransform: false
      });
    } catch {
      /* A throwing tag must not reach the caller. */
    }
  };

  if (dataMappingInterface()) {
    send();
    return;
  }

  let ticks = 0;
  const poll = setInterval(() => {
    ticks += 1;
    if (ticks > DMI_MAX_TICKS) {
      clearInterval(poll);
    }
    if (dataMappingInterface()) {
      clearInterval(poll);
      send();
    }
  }, DMI_POLL_MS);
}
